import { Inject, Injectable, PLATFORM_ID } from '@angular/core';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { forkJoin, Observable, of } from 'rxjs';
import { catchError, map } from 'rxjs/operators';
import { isPlatformBrowser } from '@angular/common';
import { environment } from '../../../../environments/environement';

export interface DashboardStats {
  totalParents: number;
  totalEducateurs: number;
  totalEleves: number;
  totalClasses: number;
  totalEmployees: number;
  elevesByClass: { label: string; count: number }[];
  parentsByMonth: { month: string; count: number }[];
}

@Injectable({ providedIn: 'root' })
export class DashboardService {
  private base = environment.apiUrl;

  constructor(
    private http: HttpClient,
    @Inject(PLATFORM_ID) private platformId: Object
  ) {}

  private getHeaders(): { [h: string]: string } {
    if (!isPlatformBrowser(this.platformId)) return {};
    const token = localStorage.getItem('presence');
    return token ? { Authorization: `Bearer ${token}` } : {};
  }

  private get etablissementId(): number {
    return Number(localStorage.getItem('etablissementId') || 0);
  }

  /** Fetch all dashboard stats in parallel */
  loadStats(): Observable<DashboardStats> {
    const id = this.etablissementId;
    const headers = this.getHeaders();

    const parents$ = this.http
      .get<any[]>(`${this.base}/users/non-archived/parents/${id}`, { headers })
      .pipe(catchError(() => of([])));

    const educateurs$ = this.http
      .get<any[]>(`${this.base}/users/non-archived/formateurs/by-departement/${id}`, { headers })
      .pipe(catchError(() => of([])));

    const eleves$ = this.http
      .get<any[]>(`${this.base}/eleves/all?etablissementId=${id}`)
      .pipe(catchError(() => of([])));

    const classes$ = this.http
      .get<any[]>(`${this.base}/api/classes?etablissementId=${id}`, { headers })
      .pipe(catchError(() => of([])));

    const employees$ = this.http
      .get<any[]>(`${this.base}/api/employees/non-archives?etablissementId=${id}`)
      .pipe(catchError(() => of([])));

    return forkJoin({ parents: parents$, educateurs: educateurs$, eleves: eleves$, classes: classes$, employees: employees$ }).pipe(
      map(({ parents, educateurs, eleves, classes, employees }) => {
        // Count pupils per class — derive from eleves list since ClasseDTO has no count field
        const elevesByClass = classes.map((c: any) => {
          const classId = c.id ?? c.classeId;
          const count = eleves.filter((e: any) =>
            (e.classeId ?? e.classe?.id) === classId
          ).length;
          return {
            label: c.nomClasse || c.nom || c.name || `Classe ${classId}`,
            count,
          };
        });

        // Group parents by creation month (last 6 months)
        const parentsByMonth = this.groupByMonth(parents);

        return {
          totalParents: parents.length,
          totalEducateurs: educateurs.length,
          totalEleves: eleves.length,
          totalClasses: classes.length,
          totalEmployees: employees.length,
          elevesByClass,
          parentsByMonth,
        };
      })
    );
  }

  private groupByMonth(users: any[]): { month: string; count: number }[] {
    const months = ['Jan', 'Fév', 'Mar', 'Avr', 'Mai', 'Jun', 'Jul', 'Aoû', 'Sep', 'Oct', 'Nov', 'Déc'];
    const now = new Date();
    const result: { month: string; count: number }[] = [];

    // Fallback: legacy users with no createdAt are attributed to the current month
    const usersWithDate = users.filter(u => u.createdAt);
    const usersNoDate = users.length - usersWithDate.length;

    for (let i = 5; i >= 0; i--) {
      const d = new Date(now.getFullYear(), now.getMonth() - i, 1);
      const label = months[d.getMonth()];
      let count = usersWithDate.filter((u) => {
        const created = new Date(u.createdAt);
        return created.getFullYear() === d.getFullYear() && created.getMonth() === d.getMonth();
      }).length;
      // Attribute legacy users to the current month so the chart is not empty
      if (i === 0 && usersNoDate > 0) count += usersNoDate;
      result.push({ month: label, count });
    }
    return result;
  }
}
